import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { FaEdit, FaPlus, FaSitemap, FaTrash } from 'react-icons/fa';
import { message } from 'antd';

import { getManagers } from '../../services/managerService';
import { createOrganizationNode, deleteOrganizationNode, getOrganizationNodes, updateOrganizationNode } from '../../services/organizationService';
import './OrganizationManagement.scss';

const emptyForm = { guid: null, parentGuid: '', managerGuid: '', name: '', title: '', nodeType: 'department', imageUrl: '', description: '', sequenceNr: 0, isActive: true };
const typeLabels = { mayor: 'Belediye Başkanı', viceMayor: 'Başkan Yardımcısı', department: 'Müdürlük', group: 'Grup/Başlık' };

const OrganizationManagement = () => {
  const [nodes, setNodes] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [organizationData, managerData] = await Promise.all([getOrganizationNodes(), getManagers()]);
      setNodes(Array.isArray(organizationData) ? organizationData : []);
      setManagers(Array.isArray(managerData) ? managerData : managerData?.data || []);
    } catch (err) {
      setError(err?.response?.data?.resultMessage || err.message || 'Organizasyon şeması yüklenemedi.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const orderedNodes = useMemo(() => {
    const children = new Map();
    nodes.forEach((node) => { const key = node.parentGuid || 'root'; children.set(key, [...(children.get(key) || []), node]); });
    children.forEach((items) => items.sort((a, b) => (a.sequenceNr - b.sequenceNr) || a.name.localeCompare(b.name, 'tr')));
    const result = [];
    const visit = (parentGuid, depth, visited = new Set()) => {
      (children.get(parentGuid || 'root') || []).forEach((node) => {
        if (visited.has(node.guid)) return;
        result.push({ ...node, depth });
        visit(node.guid, depth + 1, new Set(visited).add(node.guid));
      });
    };
    visit(null, 0); return result;
  }, [nodes]);

  const openCreate = () => { setForm(emptyForm); setShowModal(true); };
  const openEdit = (node) => {
    setForm({ ...emptyForm, ...node, parentGuid: node.parentGuid || '', managerGuid: node.managerGuid || '', imageUrl: node.imageUrl || '', description: node.description || '' });
    setShowModal(true);
  };

  const submit = async (event) => {
    event.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, parentGuid: form.parentGuid || null, managerGuid: form.managerGuid || null, sequenceNr: Number(form.sequenceNr) || 0 };
      const response = form.guid ? await updateOrganizationNode(payload) : await createOrganizationNode(payload);
      if (response?.isSuccess === false) throw new Error(response.resultMessage);
      message.success(form.guid ? 'Organizasyon kaydı güncellendi.' : 'Organizasyon kaydı eklendi.');
      setShowModal(false); await load();
    } catch (err) { message.error(err?.response?.data?.resultMessage || err.message || 'Kayıt işlemi başarısız.'); }
    finally { setSaving(false); }
  };

  const remove = async (node) => {
    if (!window.confirm(`“${node.name}” kaydını pasif yapmak istiyor musunuz?`)) return;
    try {
      const response = await deleteOrganizationNode(node.guid);
      if (response?.isSuccess === false) throw new Error(response.resultMessage);
      message.success('Organizasyon kaydı pasif yapıldı.'); await load();
    } catch (err) { message.error(err?.response?.data?.resultMessage || err.message || 'Kayıt silinemedi.'); }
  };

  return <div className="organization-management">
    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
      <div><h2 className="mb-1"><FaSitemap className="me-2" />Organizasyon Şeması</h2><p className="text-muted mb-0">Hiyerarşiyi, müdürlükleri ve yönetici bağlantılarını yönetin.</p></div>
      <Button onClick={openCreate}><FaPlus className="me-2" />Yeni Kayıt</Button>
    </div>
    {error && <Alert variant="danger">{error}</Alert>}
    <Card className="shadow-sm border-0"><Card.Body className="p-0">
      {loading ? <div className="text-center p-5"><Spinner animation="border" /></div> : <div className="table-responsive"><Table hover className="mb-0 align-middle">
        <thead><tr><th>Organizasyon Kaydı</th><th>Tür</th><th>Bağlı Müdür</th><th>Sıra</th><th>Durum</th><th /></tr></thead>
        <tbody>{orderedNodes.map((node) => <tr key={node.guid}>
          <td><div className="organization-node-name" style={{ paddingLeft: `${node.depth * 26}px` }}>{node.depth > 0 && <span className="tree-branch">↳</span>}<div><strong>{node.name}</strong>{node.title && <small>{node.title}</small>}</div></div></td>
          <td><Badge bg="info">{typeLabels[node.nodeType] || node.nodeType}</Badge></td><td>{node.manager?.name || <span className="text-muted">Atanmamış</span>}</td>
          <td>{node.sequenceNr}</td><td><Badge bg={node.isActive ? 'success' : 'secondary'}>{node.isActive ? 'Aktif' : 'Pasif'}</Badge></td>
          <td className="text-end text-nowrap"><Button variant="outline-primary" size="sm" className="me-2" onClick={() => openEdit(node)}><FaEdit /></Button><Button variant="outline-danger" size="sm" onClick={() => remove(node)}><FaTrash /></Button></td>
        </tr>)}{!orderedNodes.length && <tr><td colSpan="6" className="text-center text-muted py-5">Henüz organizasyon kaydı yok.</td></tr>}</tbody>
      </Table></div>}
    </Card.Body></Card>

    <Modal show={showModal} onHide={() => !saving && setShowModal(false)} size="lg" centered><Form onSubmit={submit}>
      <Modal.Header closeButton><Modal.Title>{form.guid ? 'Organizasyon Kaydını Düzenle' : 'Yeni Organizasyon Kaydı'}</Modal.Title></Modal.Header>
      <Modal.Body><Row className="g-3">
        <Col md={6}><Form.Group><Form.Label>Tür *</Form.Label><Form.Select value={form.nodeType} onChange={(e) => setForm({ ...form, nodeType: e.target.value })} required>{Object.entries(typeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Form.Select></Form.Group></Col>
        <Col md={6}><Form.Group><Form.Label>Üst Kayıt</Form.Label><Form.Select value={form.parentGuid} onChange={(e) => setForm({ ...form, parentGuid: e.target.value })}><option value="">Ana seviye</option>{orderedNodes.filter((node) => node.guid !== form.guid).map((node) => <option key={node.guid} value={node.guid}>{'— '.repeat(node.depth)}{node.name}</option>)}</Form.Select></Form.Group></Col>
        <Col md={7}><Form.Group><Form.Label>Ad *</Form.Label><Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={200} required /></Form.Group></Col>
        <Col md={5}><Form.Group><Form.Label>Ünvan</Form.Label><Form.Control value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={200} /></Form.Group></Col>
        <Col md={8}><Form.Group><Form.Label>Bağlı Müdür</Form.Label><Form.Select value={form.managerGuid} onChange={(e) => setForm({ ...form, managerGuid: e.target.value })}><option value="">Müdür atanmamış</option>{managers.map((manager) => <option key={manager.guid} value={manager.guid}>{manager.name} — {manager.positionName}</option>)}</Form.Select><Form.Text>Müdürün görev, iletişim, misyon ve vizyon bilgileri Müdürlerimiz ekranından yönetilir.</Form.Text></Form.Group></Col>
        <Col md={4}><Form.Group><Form.Label>Sıra</Form.Label><Form.Control type="number" min="0" value={form.sequenceNr} onChange={(e) => setForm({ ...form, sequenceNr: e.target.value })} /></Form.Group></Col>
        <Col md={12}><Form.Group><Form.Label>Görsel URL</Form.Label><Form.Control value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} maxLength={1000} /></Form.Group></Col>
        <Col md={12}><Form.Group><Form.Label>Açıklama</Form.Label><Form.Control as="textarea" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={2000} /></Form.Group></Col>
        <Col md={12}><Form.Check type="switch" label="Aktif" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /></Col>
      </Row></Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>Vazgeç</Button><Button type="submit" disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Button></Modal.Footer>
    </Form></Modal>
  </div>;
};

export default OrganizationManagement;
